import { useEffect } from "react";
import {
  Button,
  Divider,
  H5,
  Small,
  Flex,
  FlexItem,
  Toggle,
} from "orcs-design-system";
import moment from "moment";
import { isEmpty, isEqual } from "lodash";
import { useApolloClient } from "@apollo/client/react";

import {
  useDateSettings,
  useSetDateSettings,
  useClearDateSettings,
} from "src/contexts/global/ApolloSettingsContext";
import { useOldestPersonIndexDate } from "src/contexts/global/GlobalContext";
import usePrevious from "src/util/usePrevious";

import {
  getTeamDetail as getTeamDetailQuery,
  getMemberCountForTeams as getMemberCountForTeamsQuery,
} from "src/pages/TeamDetailPage/team.graphql";
import { getPersonQuery } from "src/queries/person.graphql";
import useWorkspaceDateFormats from "src/hooks/useWorkspaceDateFormats";
import { peopleSearch as peopleSearchQuery } from "src/pages/PeopleListPage/peopleSearch.graphql";
import { listTeamsInTeamListPage as listTeamsQuery } from "src/pages/TeamListPage/queries.graphql";
import { getValueGraph as getValueGraphQuery } from "src/queries/valueGraph.graphql";
import CompareDateRangePicker from "./CompareDateRangePicker";
import SingleDatePicker from "./SingleDatePicker";

const QUERIES_TO_REFETCH = [
  getTeamDetailQuery,
  getMemberCountForTeamsQuery,
  getPersonQuery,
  peopleSearchQuery,
  listTeamsQuery,
  getValueGraphQuery,
];

const HistoricalDataSidebarContent = () => {
  const dateFormats = useWorkspaceDateFormats();
  const dateSettings = useDateSettings();
  const setDateSettings = useSetDateSettings();
  const clearDateSettings = useClearDateSettings();

  const oldestPersonIndexDate = useOldestPersonIndexDate();
  const oldestPersonIndexDateMoment = oldestPersonIndexDate
    ? moment(oldestPersonIndexDate)
    : moment();

  const isDateSettingsActive = !isEmpty(dateSettings);

  const apolloClient = useApolloClient();

  // TODO: usePrevious (untyped JS) infers its own return type as always
  // `null`, since its internal `useState(null)` is never widened - a
  // pre-existing typing gap in that shared hook, outside this conversion's
  // scope. Cast to the type it actually returns at runtime.
  const prevDateSettings = usePrevious(dateSettings) as
    | typeof dateSettings
    | null;
  useEffect(() => {
    const hasChanged = !isEqual(prevDateSettings?.toDate, dateSettings?.toDate);

    // If prevDateSettings is undefined then the component will just have mounted and we don't want to refetch
    if (prevDateSettings && hasChanged) {
      void apolloClient.refetchQueries({ include: QUERIES_TO_REFETCH });
    }
  }, [dateSettings, prevDateSettings, apolloClient]);

  return (
    <Flex flexDirection="column" gap="r">
      <H5 fontWeight="bold">Historical data</H5>
      <Divider light />
      <Toggle
        small
        id="compareModeToggle"
        label="Compare mode"
        checked={dateSettings.comparisonMode === true}
        onChange={(e) => {
          setDateSettings({ comparisonMode: e.target.checked });
        }}
      />
      <Small>
        Turning on compare mode will allow you to compare two dates in the past.
      </Small>
      {dateSettings.comparisonMode ? (
        <CompareDateRangePicker
          dateSettings={dateSettings}
          setDateSettings={setDateSettings}
          dateFormats={dateFormats}
          oldestPersonIndexDateMoment={oldestPersonIndexDateMoment}
        />
      ) : (
        <SingleDatePicker
          dateSettings={dateSettings}
          setDateSettings={setDateSettings}
          dateFormats={dateFormats}
          oldestPersonIndexDateMoment={oldestPersonIndexDateMoment}
        />
      )}
      {isDateSettingsActive && (
        <FlexItem flex="0 0 auto">
          <Button small onClick={clearDateSettings}>
            Clear
          </Button>
        </FlexItem>
      )}
    </Flex>
  );
};

export default HistoricalDataSidebarContent;
